import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Shield, FileText, Search, Filter, Download, Calendar, Clock, User, Activity, AlertCircle, CheckCircle2, Eye, RefreshCw, Trash2, Settings, BarChart3, PieChart, LineChart, TrendingUp, TrendingDown
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const AuditLogsManagement = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditMetrics, setAuditMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [logFilter, setLogFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadAuditData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const logsResponse = await new AdminService().getAuditLogs(timeRange, logFilter, searchTerm);
      const metricsResponse = await new AdminService().getAuditMetrics(timeRange);

      if (logsResponse.success && metricsResponse.success) {
        setAuditLogs(logsResponse.data);
        setAuditMetrics(metricsResponse.data);
      } else {
        setError(logsResponse.message || metricsResponse.message);
      }
    } catch (err) {
      setError('Failed to fetch audit data.');
      console.error('Error fetching audit data:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange, logFilter, searchTerm]);

  useEffect(() => {
    loadAuditData();
    const interval = setInterval(loadAuditData, 60000);
    return () => clearInterval(interval);
  }, [loadAuditData]);

  const handleExportLogs = async () => {
    if (window.confirm('Are you sure you want to export audit logs?')) {
      try {
        const response = await new AdminService().exportAuditLogs(timeRange, logFilter);
        if (response.success) {
          alert('Audit logs exported successfully!');
        } else {
          alert(`Failed to export logs: ${response.message}`);
        }
      } catch (err) {
        alert('Error exporting logs.');
        console.error('Error exporting logs:', err);
      }
    }
  };

  const handleDeleteLogs = async () => {
    if (window.confirm('Are you sure you want to delete old audit logs? This action cannot be undone.')) {
      try {
        const response = await new AdminService().deleteOldAuditLogs();
        if (response.success) {
          alert('Old audit logs deleted successfully!');
          loadAuditData();
        } else {
          alert(`Failed to delete logs: ${response.message}`);
        }
      } catch (err) {
        alert('Error deleting logs.');
        console.error('Error deleting logs:', err);
      }
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-blue-600 bg-blue-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'text-green-600 bg-green-100';
      case 'update': return 'text-blue-600 bg-blue-100';
      case 'delete': return 'text-red-600 bg-red-100';
      case 'login': return 'text-purple-600 bg-purple-100';
      case 'logout': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading audit logs...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">Error: {error}</div>;
  }

  if (!auditMetrics) {
    return <div className="p-6 text-center text-gray-500">No audit data available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Shield className="mr-3 text-blue-600" size={30} />
          Audit Logs Management
        </h1>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <select
            value={logFilter}
            onChange={(e) => setLogFilter(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Logs</option>
            <option value="security">Security</option>
            <option value="user">User Actions</option>
            <option value="system">System Events</option>
            <option value="data">Data Changes</option>
          </select>
          <button
            onClick={handleExportLogs}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md shadow-md hover:bg-green-700"
          >
            <Download className="mr-2" size={18} /> Export
          </button>
          <button
            onClick={loadAuditData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700"
          >
            <RefreshCw className="mr-2" size={18} /> Refresh
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-8">
        Monitor and manage system audit logs for security, compliance, and activity tracking.
        <span className="ml-2 text-sm text-gray-500">Last Updated: {new Date(auditMetrics.lastUpdated).toLocaleString()}</span>
      </p>

      {/* Audit Overview Metrics */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <LayoutDashboard className="mr-2 text-purple-600" size={24} /> Audit Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {auditMetrics.overviewMetrics.map((metric, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-lg font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{metric.value}</p>
                <div className="flex items-center mt-2 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSeverityColor(metric.severity)}`}>
                    {metric.severity}
                  </span>
                  <span className="ml-3 text-gray-500">{metric.description}</span>
                </div>
              </div>
              {metric.type === 'total_logs' && <FileText size={40} className="text-blue-500 opacity-70" />}
              {metric.type === 'security_events' && <Shield size={40} className="text-red-500 opacity-70" />}
              {metric.type === 'user_actions' && <User size={40} className="text-green-500 opacity-70" />}
              {metric.type === 'system_events' && <Activity size={40} className="text-purple-500 opacity-70" />}
            </div>
          ))}
        </div>
      </section>

      {/* Audit Logs Table */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <FileText className="mr-2 text-green-600" size={24} /> Audit Logs
        </h2>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">System Audit Trail</h3>
            <button
              onClick={handleDeleteLogs}
              className="flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Trash2 className="mr-2" size={16} /> Clean Old Logs
            </button>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resource
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">No audit logs found.</td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.user}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.resource}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(log.severity)}`}>
                        {log.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.ipAddress}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.details}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Audit Analysis */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4 flex items-center">
          <BarChart3 className="mr-2 text-indigo-600" size={24} /> Audit Analysis
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Activity Trends</h3>
            <div className="space-y-3">
              {auditMetrics.activityTrends.map((trend, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{trend.name}</span>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{trend.value}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${trend.change > 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
                      {trend.change > 0 ? '+' : ''}{trend.change}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Security Events</h3>
            <div className="space-y-3">
              {auditMetrics.securityEvents.map((event, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{event.name}</span>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900 mr-2">{event.count}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(event.severity)}`}>
                      {event.severity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AuditLogsManagement;